/** Root crashes send one bounded technical report, without arbitrary error text. */
import { getCurrentEnvironment } from '../config/nakama.config';
import { getViewerUserId } from '../domains/current-team';
import { useMatchStore } from '../domains/match';
import i18n from '../i18n/config';
import { version } from '../version';

interface CrashReport {
  id: string;
  kind: string;
  summary: string;
  frames: string[];
  components: string[];
  version: string;
  build: string;
  language: string;
  matchId: string | null;
  playerId: string | null;
}

/** Public transport: no cookie, access code, token or session is sent. The gallery stands it in. */
export const crashReports = {
  async send(report: CrashReport): Promise<void> {
    const environment = getCurrentEnvironment();
    const host = environment === 'local' ? 'http://localhost:3000' : `https://portal-backend${environment === 'production' ? '' : `-${environment}`}.playponder.com`;
    await fetch(`${host}/client-crashes`, {
      method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report), keepalive: true, signal: AbortSignal.timeout(5000),
    });
  },
};

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
function supportId(): string {
  const bytes = new Uint8Array(16);
  try { crypto.getRandomValues(bytes); }
  catch { for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256); }
  return Array.from(bytes, (value) => ALPHABET[value & 31]).join('').match(/.{4}/g)!.join('-');
}

/** Source locations only: strip hosts, queries, messages, function arguments and local disk paths. */
function framesOf(stack: string, header: string): string[] {
  const trace = header && stack.startsWith(header) ? stack.slice(header.length) : stack;
  return trace.split('\n').slice(0, 64).flatMap((line) => {
    // V8 uses "at"; Firefox/Safari start directly with function@URL, without
    // a message header. A URL embedded in arbitrary prose is not a frame.
    if (!/^\s*(?:at\s+|[\w$.[\]<>]*@https?:\/\/)/.test(line)) return [];
    const frame = line.match(/https?:\/\/[^/\s]+\/((?:assets|src)\/[\w/.-]+\.(?:[cm]?js|tsx?))(?:\?[^\s:)]*)?:(\d{1,7}):(\d{1,7})/);
    const value = frame ? `${frame[1]}:${frame[2]}:${frame[3]}` : '';
    return value && value.length <= 200 ? [value] : [];
  }).slice(0, 32);
}

/** Broken error getters or stores cannot prevent a minimal technical report. */
function readString(read: () => unknown): string {
  try { const value = read(); return typeof value === 'string' ? value : ''; }
  catch { return ''; }
}

/** The reference exists before React draws its fallback; sending stays in componentDidCatch. */
export function createCrashReporter(send: (report: CrashReport) => Promise<void> = (report) => crashReports.send(report)) {
  const id = supportId();
  let sent = false;
  return {
    id,
    send(error: unknown, componentStack: string) {
      if (sent) return;
      sent = true;
      // Reporting must not replace the player's recovery sheet with another error.
      try {
        const message = readString(() => error instanceof Error ? error.message : '');
        const name = readString(() => error instanceof Error ? error.name : '');
        const kind = ['Error', 'TypeError', 'ReferenceError', 'RangeError', 'SyntaxError', 'URIError'].includes(name) ? name : 'Error';
        const summary = /Cannot read properties of null/.test(message) ? 'null-access'
          : /Cannot read properties of undefined/.test(message) ? 'undefined-access'
          : /is not a function/.test(message) ? 'not-callable'
          : /Maximum call stack/.test(message) ? 'stack-overflow'
          : /Failed to fetch dynamically imported module|Loading chunk/.test(message) ? 'chunk-load' : 'render-error';
        const matchId = readString(() => useMatchStore.getState().matchId);
        const playerId = readString(getViewerUserId);
        const language = i18n.resolvedLanguage?.split('-')[0] ?? 'en';
        void send({
          id, kind, summary, frames: framesOf(readString(() => error instanceof Error ? error.stack : ''), `${name}${message ? `: ${message}` : ''}`),
          components: componentStack.split('\n').flatMap((line) => line.match(/^\s*at ([A-Z][A-Za-z0-9_$]{0,79})(?:\s|$)/)?.[1] ?? []).slice(0, 32),
          version: version.semantic_version, build: version.short_sha,
          language: language === 'es' || language === 'pt' ? language : 'en',
          matchId: matchId && /^[a-f0-9-]{36}(?:\.[A-Za-z0-9_-]{1,64})?$/.test(matchId) ? matchId : null,
          playerId: playerId && /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/.test(playerId) ? playerId : null,
        }).catch(() => { /* Offline or old backend: Reload remains available. */ });
      } catch { /* A broken store or reporting transport must not break the fallback. */ }
    },
  };
}
